import { useCallback, useEffect, useMemo, useState } from 'react';
import { NSLabel } from 'bricks';
import NSProFormaInput from 'bricks/NSProFormaInput/NSProFormaInput';
import { numeralFormatter } from 'ns_libs/formatter';
import MetricOutputCard from '../../../MetricOutputCard/MetricOutputCard';
import ResidentialLotMixTable from '../ResidentialLotMixTable/ResidentialLotMixTable';
import { useProFormaSocketContext } from '../../socketContext/ProFormaSocketProvider';
import './LandDevSizeDetails.scss';

const computeTotals = (lotMixes: { lotCount?: number | null; totalFrontFootage?: number | null }[]) => ({
    totalLots: lotMixes.reduce((sum, row) => sum + (row.lotCount || 0), 0),
    totalFrontFootage: lotMixes.reduce((sum, row) => sum + (row.totalFrontFootage || 0), 0),
});

const LandDevSizeDetails = () => {
    const {
        proForma,
        residentialLotMixes,
        handleUpdateProForma,
        handleCreateResidentialLotMix,
        handleUpdateResidentialLotMix,
        handleDeleteResidentialLotMix,
    } = useProFormaSocketContext();

    const lotMixes = useMemo(
        () => residentialLotMixes?.residentialLotMixes ?? [],
        [residentialLotMixes?.residentialLotMixes],
    );
    const [siteAcreage, setSiteAcreage] = useState(proForma?.siteAcreage ?? '');
    const [displayTotals, setDisplayTotals] = useState(() => computeTotals(lotMixes));

    useEffect(() => {
        setSiteAcreage(proForma?.siteAcreage ?? '');
    }, [proForma?.siteAcreage]);

    useEffect(() => {
        setDisplayTotals(computeTotals(lotMixes));
    }, [lotMixes]);

    const handleTotalsChange = useCallback((totals: { totalLots: number; totalFrontFootage: number }) => {
        setDisplayTotals(totals);
    }, []);

    const onSiteAcreageBlur = (value: string | number) => {
        // NSProFormaInput coerces an empty number field to 0 on blur, so treat 0/empty as "cleared" (null).
        const numericValue = Number(value);
        const nextValue = numericValue ? String(numericValue) : null;
        setSiteAcreage(nextValue ?? '');
        handleUpdateProForma!([{ key: 'siteAcreage', value: nextValue }]);
    };

    return (
        <>
            <div className="d-flex align-items-start mb-3 LandDevSizeDetails__summary-row">
                <div className="d-flex align-items-center LandDevSizeDetails__site-acreage">
                    <NSLabel className="pl-0 mb-0 mr-2 text-nowrap font-weight-normal text-white font-12">
                        Site acreage
                    </NSLabel>
                    <NSProFormaInput
                        name="siteAcreage"
                        type="number"
                        value={siteAcreage}
                        handleUpdateValue={onSiteAcreageBlur}
                        placeholder="Enter site acreage"
                        inputGroupClassName="p-0 LandDevSizeDetails__acreage-input-group"
                        customClassName="LandDevSizeDetails__acreage-input font-14 text-muted"
                    />
                </div>
                <div className="d-flex flex-grow-1 justify-content-end">
                    <div className="LandDevSizeDetails__metric-card mr-2">
                        <MetricOutputCard
                            cardClassName="p-3 d-flex flex-column justify-content-center"
                            label={<span className="font-12 text-muted">Total front footage</span>}
                            value={(
                                <span className="font-24 mb-0 text-dark d-block">
                                    {`${numeralFormatter(displayTotals.totalFrontFootage, '0,0')} ft`}
                                </span>
                            )}
                        />
                    </div>
                    <div className="LandDevSizeDetails__metric-card">
                        <MetricOutputCard
                            cardClassName="p-3 d-flex flex-column justify-content-center"
                            label={<span className="font-12 text-muted">Total lot count</span>}
                            value={(
                                <span className="font-24 mb-0 text-dark d-block">
                                    {numeralFormatter(displayTotals.totalLots, '0,0')}
                                </span>
                            )}
                        />
                    </div>
                </div>
            </div>

            <ResidentialLotMixTable
                lotMixes={lotMixes}
                onCreate={() => handleCreateResidentialLotMix!()}
                onUpdate={(lotMixId, dataKey, value) => handleUpdateResidentialLotMix!(lotMixId, dataKey, value)}
                onDelete={lotMixId => handleDeleteResidentialLotMix!(lotMixId)}
                onTotalsChange={handleTotalsChange}
            />
        </>
    );
};

export default LandDevSizeDetails;
